<script>
import { h } from "vue"
import icons from "~/icons.json"

export default {
    props: {
        name: {
            type: String,
            required: true,
            validator(val) {
                if (val && !(val in icons)) {
                    console.warn(
                        `Invalid prop: prop "name" is referring to an unregistered icon "${val}".` +
                            `\nPlease make sure you have imported this icon before using it.`
                    )
                    //return false
                }
                return true
            },
        },

        title: String,
        spin: Boolean,
        w: Number,
        h: Number,
        flip: {
            validator(val) {
                return val === "horizontal" || val === "vertical"
            },
        },

        label: String,
        tabindex: [Number, String],
    },

    data() {
        return {
            id: getId(),
            x: false,
            y: false,
        }
    },

    computed: {
        klass() {
            return {
                "fa-icon": true,
                "fa-flip-horizontal": this.flip === "horizontal",
                "fa-flip-vertical": this.flip === "vertical",
                "fa-spin": this.spin,
                "flex-shrink-0": true,
            }
        },

        icon() {
            if (this.name) {
                return icons[this.name]
            }
            return null
        },

        box() {
            if (!this.icon) {
                console.log(`${this.name} was not found`)
                return `0 0 24 24`
            }
            return `0 0 ${this.icon.width} ${this.icon.height}`
        },

        raw() {
            // generate unique id for each icon's SVG element with ID
            if (!this.icon || !this.icon.raw) {
                return null
            }
            let raw = this.icon.raw
            let ids = {}
            raw = raw.replace(
                /\s(?:xml:)?id=(["']?)([^"')\s]+)\1/g,
                (match, quote, id) => {
                    let uniqueId = getId()
                    ids[id] = uniqueId
                    return ` id="${uniqueId}"`
                }
            )
            raw = raw.replace(
                /#(?:([^'")\s]+)|xpointer\(id\((['"]?)([^')]+)\2\)\))/g,
                (match, rawId, _, pointerId) => {
                    let id = rawId || pointerId
                    if (!id || !ids[id]) {
                        return match
                    }

                    return `#${ids[id]}`
                }
            )

            return raw
        },

        focusable() {
            let { tabindex } = this
            if (tabindex == null) {
                return "false"
            }
            let index =
                typeof tabindex === "string" ? parseInt(tabindex, 10) : tabindex
            if (index >= 0) {
                return null
            }
            return "false"
        },

        style() {
            if (this.w && this.h) {
                return {
                    width: `${this.w}px`,
                    height: `${this.h}px`,
                }
            } else if (this.w) {
                return {
                    width: `${this.w}px`,
                    height: `${this.w}px`,
                }
            } else if (this.h) {
                return {
                    width: `${this.h}px`,
                    height: `${this.h}px`,
                }
            } else {
                return {}
            }
        },
    },

    mounted() {
        this.updateStack()
    },

    updated() {
        this.updateStack()
    },

    methods: {
        updateStack() {
            const children = Array.from(this.$el.children ?? [])
            if (!this.name && this.name !== null && children.length === 0) {
                console.warn('Invalid prop: prop "name" is required.')
                return
            }

            if (this.icon) {
                return
            }

            let width = 0
            let height = 0
            children.forEach(child => {
                width = Math.max(width, child.width)
                height = Math.max(height, child.height)
            })
            children.forEach(child => {
                child.x = (width - child.width) / 2
                child.y = (height - child.height) / 2
            })
        },
    },

    render() {
        if (this.name === null || !this.icon) {
            return h("span", this.name)
        }

        let options = {
            class: this.klass,
            style: this.style,
            role: this.$attrs.role || (this.label || this.title ? "img" : null),
            "aria-label": this.label || null,
            "aria-hidden": String(!(this.label || this.title)),
            tabindex: this.tabindex,
            viewBox: this.box,
            focusable: this.focusable,
        }

        let titleId = `vat-${this.id}`
        if (this.title) {
            options["aria-labelled-by"] = titleId
        }

        if (this.raw) {
            let html = this.raw

            if (this.title) {
                html = `<title id="${titleId}">${escapeHTML(this.title)}</title>${html}`
            }

            options.innerHTML = html
        }

        let content = this.title
            ? [h("title", { id: titleId }, this.title)]
            : []

        const { paths = [], polygons = [] } = this.icon
        return h(
            "svg",
            options,
            this.raw
                ? null
                : content.concat([
                      ...paths.map((path, i) =>
                          h("path", {
                              ...path,
                              key: `path-${i}`,
                          })
                      ),
                      ...polygons.map((polygon, i) =>
                          h("polygon", {
                              ...polygon,
                              key: `polygon-${i}`,
                          })
                      ),
                  ])
        )
    },

    register(data) {
        for (let name in data) {
            let icon = data[name]
            let { paths = [], d, polygons = [], points } = icon

            if (d) {
                paths.push({ d })
            }

            if (points) {
                polygons.push({ points })
            }

            icons[name] = assign({}, icon, {
                paths,
                polygons,
            })
        }
    },

    icons,
}

function assign(obj, ...sources) {
    sources.forEach(source => {
        for (let key in source) {
            if (Object.hasOwn(source, key)) {
                obj[key] = source[key]
            }
        }
    })

    return obj
}

let cursor = 0xd4937

function getId() {
    return `va-${(cursor++).toString(16)}`
}

const ESCAPE_MAP = {
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "&": "&amp;",
}

function escapeHTML(html) {
    return html.replace(/[<>"&]/g, c => ESCAPE_MAP[c] || c)
}
</script>

<style scoped>
.fa-icon {
    display: inline-block;
    fill: currentColor;
}

.fa-flip-horizontal {
    transform: scaleX(-1);
}

.fa-flip-vertical {
    transform: scaleY(-1);
}

.fa-spin {
    animation: fa-spin 0.65s 0s infinite linear;
}

@keyframes fa-spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
